import React, {useContext, useEffect, useRef, useState} from "react";
import {Text, Pressable} from "react-native";
import Input from "./Input";
import MainStyle from "../../MainStyle";
import Button from "./Button";
import * as Yup from "yup";
import {useFormik} from "formik";
import {formatWithMask, useMaskedInputProps} from "react-native-mask-input";
import {isFullSwing} from "../constants";
import {StoreContext} from "./StoreContextProvider";
import {colors} from "../assets/styles/colors";
import Valid from "card-validator";
import {useNavigation} from "@react-navigation/native";
import {CancelModalProps} from "../types";
import {formatCurrency, getFieldError} from "../utils";
import Div from "../lib/Div";
import {TextInput, Checkbox} from "react-native-paper";
import AppCheckbox from "./Checkbox";
import P from "../lib/P";
import Btn from "./Btn";
import Spinner from "react-native-loading-spinner-overlay";
import {NativeStackNavigationProp} from "@react-navigation/native-stack";
import CancelModal from "../screens/Auth/SignUp/steps/CancelModal";
import {useCardTokenization} from "../hooks/useCardTokenization";
import {
  getCardDepositErrorCause, getCardDepositErrorInfo, isAmbiguousCardDepositError, submitCardDeposit
} from "../services/cardDeposit";
import {
  buildSafeCardTokenizationErrorLog, cardTokenizationPreparationErrorMessage, isCardTokenizationPreparationError
} from "../services/cardTokenization";

const initialValues = {number: '', expiry: '', cvc: '', name: ''};
const validationSchema = Yup.object({
  number: Yup.string().required(), expiry: Yup.string().required('Required Field').min(5), cvc: Yup.string().required(),
  name: Yup.string()
});

const numberMask = [/\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, /\d/, /\d/];
const expiryMask = [/\d/, /\d/, '/', /\d/, /\d/];
const cvcMask = [/\d/, /\d/, /\d/];

type Props = {
  amountCents: number, goBack: () => void, active?: boolean, onSubmittingChange?: (isSubmitting: boolean) => void
};

const OnlinePayment = (props: Props) => {
  const {amountCents, active = true} = props;
  const Context = useContext(StoreContext);
  const {user} = Context;
  const navigation: NativeStackNavigationProp<any> = useNavigation();
  const [isChecked, setIsChecked] = useState(false);
  const [loadingOverlay, setLoadingOverlay] = useState(false);
  const [cancelModalConfig, setCancelModalConfig] = useState<CancelModalProps>({
    visible: false, title: '', subtitle: ''
  });
  const activeDepositSubmissionRef = useRef(false);
  const {
    status: tokenizationStatus, errorMessage: tokenizationError, retry: retryTokenization, tokenizeCard
  } = useCardTokenization(active);

  const closeCancelModal = () => {
    setCancelModalConfig(current => ({...current, visible: false}));
  };

  const showErrorModal = (subtitle: string, onDismiss?: () => void, buttonText = 'OK') => {
    setCancelModalConfig({
      visible: true, title: 'Error', subtitle, rightBtnText: buttonText,
      onResume: () => {
        closeCancelModal();
        onDismiss?.();
      }
    });
  };

  const formik = useFormik({
    initialValues, validationSchema,
    onSubmit: () => {
      if (user && user.selfLimitationConfirmed) {
        setCancelModalConfig({
          visible: true, title: 'Notice', titleColor: MainStyle.c_yellow, leftBtnText: 'OK',
          subtitle: 'You will not be able to play or deposit money while the Self-Limitation is in effect.',
          onExit: () => {
            if (isFullSwing) props.goBack();
            closeCancelModal();
            navigation.navigate('SelfExclusion');
          }
        });

      } else {
        return submitDeposit();
      }
    }
  });
  const numberValidation = Valid.number(formik.values.number);
  let cardNumberMask = numberMask;
  let codeSizeMask = cvcMask;

  if (numberValidation.card) {
    const {gaps, lengths, code} = numberValidation.card;
    cardNumberMask = [];
    codeSizeMask = [];

    for (let i = 0; i < lengths[lengths.length - 1]; i++) {
      if (gaps.includes(i)) cardNumberMask.push(' ');
      cardNumberMask.push(/\d/);
    }

    for (let i = 0; i < code.size; i++) {
      codeSizeMask.push(/\d/);
    }
  }

  const numberMaskedInputProps = useMaskedInputProps({
    value: formik.values.number, onChangeText: formik.handleChange('number'), mask: cardNumberMask
  });

  const expiryMaskedInputProps = useMaskedInputProps({
    value: formik.values.expiry, onChangeText: formik.handleChange('expiry'), mask: expiryMask
  });

  const codeMaskedInputProps = useMaskedInputProps({
    value: formik.values.cvc, onChangeText: formik.handleChange('cvc'), mask: codeSizeMask
  });

  const submitDeposit = async () => {
    if (activeDepositSubmissionRef.current || loadingOverlay) return;

    if (tokenizationStatus !== 'ready') {
      if (tokenizationStatus === 'error') {
        showErrorModal(tokenizationError || cardTokenizationPreparationErrorMessage, retryTokenization, 'RETRY');

      } else {
        showErrorModal('Secure card entry is still preparing. Please try again in a moment.');
      }
      return;
    }

    const {unmasked: number} = formatWithMask({text: formik.values.number, mask: cardNumberMask});
    const {unmasked: code} = formatWithMask({text: formik.values.cvc, mask: codeSizeMask});
    const {unmasked: expiry} = formatWithMask({text: formik.values.expiry, mask: expiryMask});
    const clearSensitiveFields = () => {
      void formik.setFieldValue('number', '', false);
      void formik.setFieldValue('cvc', '', false);
    };

    activeDepositSubmissionRef.current = true;
    setLoadingOverlay(true);
    props.onSubmittingChange?.(true);

    try {
      await submitCardDeposit({
        amountCents, saveCard: isChecked, cardNumber: number, cvc: code, expirationDate: expiry, tokenizeCard,
        clearSensitiveFields
      });
      void Context.getUser().catch(err => {
        const errorInfo = getCardDepositErrorInfo(err);
        console.log('deposit balance refresh error', {message: errorInfo.serverMessage || errorInfo.message});
      });

      setCancelModalConfig({
        visible: true, title: 'Success', titleColor: MainStyle.c_green, leftBtnText: 'OK',
        subtitle: 'You have successfully replenished your balance.',
        onExit: () => {
          if (isFullSwing) props.goBack();
          closeCancelModal();
          navigation.navigate('challenges');
        }
      });

    } catch (err) {
      const cause = getCardDepositErrorCause(err);

      if (isCardTokenizationPreparationError(cause)) {
        console.warn('deposit card tokenization preparation error', buildSafeCardTokenizationErrorLog(cause));
        showErrorModal(cardTokenizationPreparationErrorMessage, retryTokenization, 'RETRY');
        return;
      }

      const errorInfo = getCardDepositErrorInfo(err);
      const ambiguousDepositError = isAmbiguousCardDepositError(err);
      console.log('deposit submission error', {message: errorInfo.serverMessage || errorInfo.message});
      showErrorModal(ambiguousDepositError ? 'We could not confirm whether your deposit was completed. Please check your balance or contact support before trying again.' : errorInfo.message, ambiguousDepositError ? props.goBack : undefined);

    } finally {
      activeDepositSubmissionRef.current = false;
      setLoadingOverlay(false);
      props.onSubmittingChange?.(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    void formik.setFieldValue('name', `${user.realName} ${user.lastname}`, false);
  }, [user]);

  useEffect(() => {
    if (active) return;
    formik.resetForm({values: {...initialValues, name: user ? `${user.realName} ${user.lastname}` : ''}});
    setIsChecked(false);
    closeCancelModal();
  }, [active]);

  return (
    <>
      <Div h={isFullSwing ? 24 : 72}/>

      {isFullSwing ? (
        <TextInput
          label="Name on Card" onBlur={formik.handleBlur('name')} style={[MainStyle.b_r_4, MainStyle.m_b_20]}
          value={formik.values.name} onChangeText={formik.handleChange('name')} underlineColor="transparent"
          editable={false}
        />
      ) : (
        <Input
          value={formik.values.name} onChangeText={formik.handleChange('name')} onBlur={formik.handleBlur('name')}
          placeholder="Cardholder’s Name" editable={false}
        />
      )}

      {isFullSwing ? (
        <TextInput
          label="Card Number" onBlur={formik.handleBlur('number')} style={[MainStyle.b_r_4, MainStyle.m_b_20]}
          error={!!(formik.touched.number && !numberValidation.isValid && 'Invalid Card Number')}
          {...numberMaskedInputProps} underlineColor="transparent" keyboardType="numeric"
        />
      ) : (
        <Input
          label="CARD NUMBER" type="numeric" onBlur={formik.handleBlur('number')}
          error={formik.touched.number && !numberValidation.isValid && 'Invalid Card Number'}
          style={[MainStyle.m_b_21, MainStyle.m_t_19]} {...numberMaskedInputProps} placeholder="1234 5678 1234 5678"
        />
      )}

      <Div row mb={isFullSwing ? 20 : 15}>
        {isFullSwing ? (
          <Div flex={1}>
            <TextInput
              keyboardType="numeric" onBlur={formik.handleBlur('expiry')}
              error={!!getFieldError('expiry', formik)} {...expiryMaskedInputProps} label="Expiration Date"
              placeholder="MM/YY" style={[MainStyle.b_r_4]} underlineColor="transparent"
            />
          </Div>
        ) : (
          <Input
            type="numeric" onBlur={formik.handleBlur('expiry')} error={getFieldError('expiry', formik)}
            {...expiryMaskedInputProps} label="EXPIRY" placeholder="MM/YY" style={MainStyle.flex1}
          />
        )}

        <Div w={isFullSwing ? 20 : 15}/>

        {isFullSwing ? (
          <Div flex={1}>
            <TextInput
              keyboardType="numeric" onBlur={formik.handleBlur('cvc')} {...codeMaskedInputProps}
              error={!!(formik.touched.cvc && numberValidation.card && formik.values.cvc.length !== numberValidation.card.code.size && 'Invalid CVC')}
              placeholder="CVC" label="CVC" style={[MainStyle.b_r_4]} underlineColor="transparent"
            />
          </Div>
        ) : (
          <Input
            type="numeric" onBlur={formik.handleBlur('cvc')} {...codeMaskedInputProps}
            error={formik.touched.cvc && numberValidation.card && formik.values.cvc.length !== numberValidation.card.code.size && 'Invalid CVC'}
            placeholder="CVC" label="CVC" style={MainStyle.flex1}
          />
        )}

        {/*<Input label="ZIP CODE" placeholder="Zip Code" w={120} type="numeric"/>*/}
      </Div>

      {isFullSwing && (
        <Div row gap={10} alignItems="center" mb={16}>
          {/* Paper's Checkbox draws via an icon source this app does not bundle,
              so its state change was invisible; the in-house checkbox is not. */}
          <AppCheckbox value={isChecked} size={24} onValueChange={() => setIsChecked(!isChecked)}/>
          <Btn onPress={() => setIsChecked(!isChecked)}>
            <P fontFamily="DavisSans-Regular">Save for future deposits</P>
          </Btn>
        </Div>
      )}

      {!isFullSwing && (
        <P mb={38} fw="600" fs={28} ta="center" mt={60} color={colors.lightBlue} lh={27}>
          {formatCurrency(amountCents / 100)}
        </P>
      )}

      {isFullSwing ? (
        <Btn bg="#3369FF" rounded={4} mb={24} disabled={loadingOverlay} opacity={loadingOverlay ? 0.6 : 1}
             onPress={() => formik.handleSubmit()}>
          <P lh={56} ta="center" fontFamily="DavisSans-Medium">Finished</P>
        </Btn>
      ) : (
        <Button
          text="DEPOSIT" onPress={() => formik.handleSubmit()} w={164} h={40} mx="auto"
          fontSize={16} mb={16} style={styles.btn_shadow}
        />
      )}

      {!isFullSwing && (
        <Button
          text="BACK" onPress={props.goBack} w={164} h={40} fontSize={16} mx="auto"
          bg={colors.unselect} style={styles.btn_shadow}
        />
      )}

      {!isFullSwing && (
        <Pressable style={[MainStyle.row, MainStyle.a_i_c, MainStyle.m_t_34, MainStyle.m_b_45, MainStyle.m_x_auto]}
                   onPress={() => setIsChecked(!isChecked)}>
          <Checkbox status={isChecked ? 'checked' : 'unchecked'} onPress={() => setIsChecked(!isChecked)}/>
          <Text style={styles.label_s}>Save Card</Text>
        </Pressable>
      )}

      <Spinner visible={loadingOverlay} overlayColor="rgba(0, 0, 0, 0.6)"/>
      <CancelModal config={cancelModalConfig}/>
    </>
  );
};

const styles = {
  amount_s: [MainStyle.m_b_38, MainStyle.f_w_600, MainStyle.f_s_28, MainStyle.c_lightblue, MainStyle.l_h_27],
  label_s: [MainStyle.f_w_600, MainStyle.f_s_14, MainStyle.c_white, MainStyle.m_l_8],
  btn_shadow: {
    "shadowOffset": {
      "width": 0,
      "height": 4
    },
    "shadowRadius": 4,
    "shadowColor": "rgba(0, 0, 0, 0.25)",
    "shadowOpacity": 1
  },
};

export default React.memo(OnlinePayment);
